"use client";

import React, { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import Player from "@vimeo/player";
import { LoaderSpinner } from "../LoaderSpinner/LoaderSpinner";
import { TVideoAsset } from "@/types/global";
import { Image } from "../Image/Image";
import * as Styled from "./Video.styled";
import { toggleGallerySliderModal } from "@/utils/generic";

export interface IVimeoVideoIframeProps extends TVideoAsset {
  includesControls?: boolean;
  autoplay?: "always" | "whenVisible" | "never";
  includesSound?: boolean; // (ignored in iframe for now)
  loop?: boolean;
  includeContainer?: boolean; // (kept for API symmetry)
  styles?: React.CSSProperties;
  includeInSlider?: boolean;
  classNameContainer?: string;
  vimeoSizeLimit?: number;
  scaleVideoToFit?: boolean;
}

export const VimeoVideo: React.FC<IVimeoVideoIframeProps> = React.memo(
  function VimeoVideo({
    includesControls = false,
    loop = true,
    vimeoId = "",
    aspectRatio = "20 / 8",
    poster,
    title,
    vimeoMetadata,
    includeInSlider = false,
    classNameContainer,
    scaleVideoToFit = true,
    // vimeoSizeLimit,
    autoplay = "whenVisible",
  }) {
    const refVimeoVideoContainer = useRef<HTMLDivElement>(null);
    const refAutoplay = useRef(autoplay);
    const refIncludesControls = useRef(includesControls);

    const iframeRef = useRef<HTMLIFrameElement>(null);
    const playerRef = useRef<Player | null>(null);
    const refPoster = useRef<HTMLImageElement>(null);

    const hidePosterAndLoader = () => {
      const loader = refVimeoVideoContainer.current?.querySelector(
        ".loader-spinner"
      ) as HTMLElement | null;
      const imagePoster = refVimeoVideoContainer.current?.querySelector(
        ".vimeo-poster, .poster-from-cms"
      ) as HTMLElement | null;
      loader?.classList.add("hidden");
      imagePoster?.classList.add("hidden");
    };

    const videoSrcIframe = useMemo(() => {
      if (refAutoplay.current === "whenVisible") {
        return refIncludesControls.current
          ? `https://player.vimeo.com/video/${vimeoId}?muted=1&loop=${
              loop ? "1" : "0"
            }&controls=1&autoplay=1&title=0&byline=0&portrait=0&autopause=0&dnt=1`
          : `https://player.vimeo.com/video/${vimeoId}?muted=1&loop=${
              loop ? "1" : "0"
            }&controls=0&title=0&byline=0&portrait=0&autopause=0&dnt=1`;
      } else {
        return `https://player.vimeo.com/video/${vimeoId}?loop=${
          loop ? "1" : "0"
        }&controls=1&title=0&byline=0&portrait=0&autopause=0&dnt=1`;
      }
    }, [loop, vimeoId]);

    // IFRAME init
    useEffect(() => {
      if (!iframeRef.current || playerRef.current) return;

      const player = new Player(iframeRef.current, { autopause: false });
      playerRef.current = player;

      const elVimeoContainer = refVimeoVideoContainer.current;
      let observer: IntersectionObserver | null = null;

      const onTimeUpdate = (data: { seconds: number }) => {
        if (data.seconds > 0) {
          hidePosterAndLoader();
          player.off("timeupdate", onTimeUpdate);
        }
      };

      const onPlaying = () => {
        setTimeout(() => {
          player
            .getCurrentTime()
            .then((t) => {
              if (t > 0) hidePosterAndLoader();
            })
            .catch(() => {});
        }, 120);
      };

      void player
        .ready()
        .then(() => {
          if (autoplay !== "never") {
            player.on("timeupdate", onTimeUpdate);
            player.on("playing", onPlaying);
          }
          if (!elVimeoContainer) return;
          if (refAutoplay.current === "whenVisible") {
            observer = new IntersectionObserver(
              ([entry]) => {
                if (entry.isIntersecting) {
                  player.setVolume(0).catch(() => {});
                  player.play().catch(() => {});
                } else {
                  player.pause().catch(() => {});
                }
              },
              { root: null, threshold: 0.01 }
            );
            observer.observe(elVimeoContainer);
          } else if (refAutoplay.current === "always") {
            player.play().catch(() => {});
          }
        })
        .catch((err) => console.error("Vimeo player ready error:", err));

      return () => {
        try {
          player.off("timeupdate", onTimeUpdate);
          player.off("playing", onPlaying);
        } catch {}
        if (observer && elVimeoContainer) {
          try {
            observer.unobserve(elVimeoContainer);
          } catch {}
          observer.disconnect();
        }
      };
    }, [autoplay]);

    // Los videos de vimeo cargados por iframe se ajustan en base a la altura
    // por eso, si el contenedor tiene diferente aspect ratio, hay que escalar el iframe
    useLayoutEffect(() => {
      const elIframe = iframeRef.current;
      const elIframeParent = elIframe?.parentElement;
      if (refIncludesControls.current || !elIframe || !elIframeParent) return;

      const parentAR =
        elIframeParent.offsetWidth / elIframeParent.offsetHeight;
      const videoAR = vimeoMetadata?.aspectRatio; // width / height

      if (videoAR && parentAR > 0) {
        // base: full height, centered
        elIframe.style.position = "absolute";
        elIframe.style.top = "50%";
        elIframe.style.left = "50%";
        elIframe.style.height = "100%";
        elIframe.style.transform = "translate(-50%, -50%)";
        elIframe.style.transformOrigin = "center center";

        if (scaleVideoToFit) {
          // how wide should the iframe be relative to its parent
          const widthPercent = (videoAR / parentAR) * 100;
          // tiny buffer so we don’t get 1px gaps from rounding
          const finalWidthPercent = widthPercent * 1.02;
          elIframe.style.width = `${finalWidthPercent}%`;
        } else {
          elIframe.style.width = "100%";
        }
      }
    }, [vimeoMetadata?.aspectRatio, scaleVideoToFit]);


    return (
      <Styled.VideoContainer
        aspectRatio={aspectRatio}
        ref={refVimeoVideoContainer}
        className={`vimeo-video-container ${classNameContainer ?? ""}`}
        onClick={
          includeInSlider && !includesControls
            ? () => toggleGallerySliderModal(vimeoId)
            : undefined
        }
      >
        <LoaderSpinner isDisplayed={true} delayDisplay={true} />

        {poster?.src && (
          <Image
            src={poster.src}
            alt={title ?? "Vimeo thumbnail"}
            fill
            sizes={{ mobile: "90vw", tablet: "40vw", desktop: "2000px" }}
            className="poster-from-cms"
            ref={refPoster}
            base64={poster.base64}
          />
        )}

        {!poster?.src && vimeoMetadata?.poster && (
          <Image
            src={vimeoMetadata.poster}
            alt={title ?? "Vimeo thumbnail"}
            fill
            sizes={{ mobile: "90vw", tablet: "40vw", desktop: "2000px" }}
            className="vimeo-poster"
            ref={refPoster}
          />
        )}

        <iframe
          key={vimeoId}
          ref={iframeRef}
          src={videoSrcIframe}
          data-aspect-ratio={aspectRatio}
          data-autoplay={autoplay}
          data-is-scaled={scaleVideoToFit}
          style={{
            position: "absolute",
            inset: 0,
            height: "100%",
            border: "none",
            zIndex: 1,
            aspectRatio: (aspectRatio as any) || "unset",
          }}
          className={includeInSlider && !includesControls ? "include-in-slider" : ""}
          {...(title ? { title } : {})}
          allow={autoplay === "whenVisible" ? "autoplay; fullscreen" : "fullscreen"}
          data-src={vimeoId}
        />
      </Styled.VideoContainer>
    );
  }
);

VimeoVideo.displayName = "VimeoVideo";
